/**
 * BriefMessageViewer — inline expansion panel for one RM/date brief on
 * BriefsPage (Phase 8).
 *
 * Surfaces the EXACT Slack content that was posted to the RM, not metadata:
 *   - Main message — Block-Kit JSON converted via slackBlocksToMarkdown,
 *     then rendered through react-markdown so headers / lists / links read
 *     properly. Falls back to slackMrkdwnToMarkdown(mainText) when blocks
 *     are missing.
 *   - Thread replies — sequential, each labelled "Reply n/m" with a left
 *     border accent so they read as a quoted continuation.
 *   - Feedback prompt — dimmed background block titled "Feedback prompt".
 *     Its action buttons (rate 1..5) render as inert chips because they
 *     were Slack interactions, not clickable here.
 *
 * Header includes:
 *   - sentAt, formatted in CET (RM-aligned)
 *   - "Open in Langfuse" link when traceId is non-null
 *   - "Copy" button — copies raw mainText (Slack mrkdwn) to clipboard.
 *
 * 404 (no row persisted) is a first-class state because older briefs were
 * sent before the brief_messages backfill window. Falls back to the
 * `traceId` from the parent row (when supplied) so admins can still inspect
 * the trace in Langfuse.
 *
 * Network: backed by `useBriefMessages` (TanStack Query). React Query caches
 * the response per `[userId, date]`, so collapse → re-expand re-uses the
 * cached payload without a second round trip.
 *
 * Mobile: prose container is `max-w-none break-words`; long URLs / inline
 * code wrap instead of forcing horizontal scroll.
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { Card, CardContent } from '@/components/ui/card';
import { useBriefMessages, useBriefMessagesByTrace } from '@/hooks/use-briefs';
import { langfuseTraceUrl } from '@/lib/langfuse';
import { formatDateTimeWithLabel } from '@/lib/format-date';
import {
  slackBlocksToMarkdown,
  slackMrkdwnToMarkdown,
} from '@/lib/slack-mrkdwn';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/services/api';
import type { BriefMessages } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface BriefMessageViewerProps {
  /** Slack username — `briefMessages.username`. */
  userId: string;
  /** YYYY-MM-DD date — `briefMessages.sentDate`. */
  date: string;
  /**
   * Trace id known from the parent row, surfaced as a Langfuse fallback link
   * when the messages endpoint returns 404 (no persisted row). Optional — the
   * status-per-user endpoint doesn't carry traceId today, but the prop is
   * here so a future richer row shape lights it up automatically.
   */
  fallbackTraceId?: string | null;
  /**
   * Gate the underlying fetch — only triggered when the row is expanded so
   * we don't hammer the endpoint for every row on the page. Defaults to
   * `true` because the parent only mounts the viewer when expanded.
   */
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Pick the richest available rendering for a Slack message — blocks first
 * (preserves headers / lists / dividers), text fallback (Slack mrkdwn) so
 * thread replies that were sent without explicit blocks still render.
 */
function pickMarkdown(blocks: unknown[] | undefined, text: string): string {
  if (Array.isArray(blocks) && blocks.length > 0) {
    const md = slackBlocksToMarkdown(blocks);
    if (md.trim().length > 0) return md;
  }
  return slackMrkdwnToMarkdown(text ?? '');
}

/**
 * Inline markdown — Tailwind `prose` styled body with link target=_blank and
 * stop-propagation so clicking a link inside the panel doesn't bubble to a
 * parent row toggle.
 *
 * Sized at `prose-sm` so the inline panel stays scannable next to the dense
 * source table; `max-w-none` lets the prose fill the expansion full width
 * (default `prose` caps at ~65ch which leaves a strange gutter on desktop).
 */
function MarkdownBody({
  markdown,
  testId,
}: {
  markdown: string;
  testId: string;
}): React.ReactElement {
  return (
    <div
      data-testid={testId}
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert break-words',
        'prose-p:my-1 prose-headings:my-2 prose-pre:my-1 prose-li:my-0',
        'prose-a:text-primary prose-a:underline-offset-2',
        'prose-code:font-mono prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none',
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          a: ({ node: _node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            />
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Action-button extraction for the feedback block. Slack feedback prompts
 * end in an `actions` block whose `elements` are buttons with a `text.text`
 * label. We surface those labels as inert chips so the reader sees what the
 * RM was offered (rate 1..5, "skip", etc.) without making them clickable.
 */
interface SlackButton {
  type?: string;
  text?: { text?: string };
}
function extractActionLabels(blocks: unknown[]): string[] {
  const labels: string[] = [];
  for (const raw of blocks) {
    if (!raw || typeof raw !== 'object') continue;
    const block = raw as { type?: string; elements?: unknown[] };
    if (block.type !== 'actions' || !Array.isArray(block.elements)) continue;
    for (const el of block.elements) {
      const btn = el as SlackButton;
      const label = btn?.text?.text;
      if (typeof label === 'string' && label.length > 0) labels.push(label);
    }
  }
  return labels;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ViewerSkeleton(): React.ReactElement {
  return (
    <div data-testid="brief-message-viewer-skeleton" className="space-y-2">
      <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
      <div className="h-3 w-full rounded bg-muted animate-pulse" />
      <div className="h-3 w-5/6 rounded bg-muted animate-pulse" />
      <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Shared state machine + canonical render for the brief viewers below.
 * Presentation is identical across surfaces; only the fetch key differs
 * (by user/date on the Briefs tab vs by trace id on every trace surface).
 */
function BriefMessageStateView({
  data,
  isLoading,
  error,
  fallbackTraceId = null,
}: {
  data: BriefMessages | undefined;
  isLoading: boolean;
  error: unknown;
  fallbackTraceId?: string | null;
}): React.ReactElement {
  // 404 → not persisted; surface as a first-class informative state instead
  // of an error card. Any other status falls into the generic error branch.
  const apiError = error as ApiError | undefined;
  const isNotFound = apiError?.status === 404;
  const isOtherError = !!apiError && !isNotFound;

  const [copied, setCopied] = React.useState(false);
  const handleCopy = React.useCallback(() => {
    if (!data?.mainText) return;
    void navigator.clipboard
      .writeText(data.mainText)
      .then(() => {
        setCopied(true);
        // Reset after a short tick so a second copy still flashes "Copied".
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard may be denied (insecure context). Swallow — the user
        // can still read the text on screen.
      });
  }, [data?.mainText]);

  // ---- Loading ----------------------------------------------------------
  if (isLoading) {
    return (
      <Card data-testid="brief-message-viewer" data-state="loading">
        <CardContent className="p-4">
          <ViewerSkeleton />
        </CardContent>
      </Card>
    );
  }

  // ---- 404: not persisted ----------------------------------------------
  if (isNotFound) {
    const traceUrl = fallbackTraceId
      ? langfuseTraceUrl(fallbackTraceId)
      : null;
    return (
      <Card data-testid="brief-message-viewer" data-state="not-found">
        <CardContent className="p-4 text-sm text-muted-foreground space-y-2">
          <p>
            Not persisted for this date — older than the backfill window, or no
            brief was sent.
          </p>
          {traceUrl && (
            <a
              href={traceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-message-viewer-fallback-trace"
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              → open in Langfuse
            </a>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---- Other error -----------------------------------------------------
  if (isOtherError) {
    return (
      <Card data-testid="brief-message-viewer" data-state="error">
        <CardContent className="p-4 text-sm text-destructive">
          Failed to load brief message: {apiError?.message ?? 'unknown error'}
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    // Defensive — TanStack Query reports loading=false + data=undefined for
    // the brief gap between `enabled=true` and the first fetch. Render a
    // tiny skeleton so the row doesn't pop empty.
    return (
      <Card data-testid="brief-message-viewer" data-state="idle">
        <CardContent className="p-4">
          <ViewerSkeleton />
        </CardContent>
      </Card>
    );
  }

  // ---- Loaded ----------------------------------------------------------
  const mainMd = pickMarkdown(data.mainBlocks, data.mainText);
  const traceUrl = data.traceId ? langfuseTraceUrl(data.traceId) : null;
  const feedbackLabels = data.feedbackBlock
    ? extractActionLabels(data.feedbackBlock)
    : [];
  const feedbackHasContent =
    !!data.feedbackBlock && data.feedbackBlock.length > 0;

  return (
    <Card data-testid="brief-message-viewer" data-state="loaded">
      {/* Mobile-first padding pass: tighter inset on phones (12px) so the
          dense Slack content keeps as much horizontal width as possible;
          desktop reverts to the previous 16px so the panel still breathes
          inside a wider parent column. */}
      <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
        {/* Header */}
        <div
          data-testid="brief-message-viewer-header"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
        >
          <span>Sent {formatDateTimeWithLabel(data.sentAt)}</span>
          <button
            type="button"
            onClick={(e) => {
              // Stop bubbling — a parent table row-toggle would otherwise
              // collapse the panel mid-copy on phones (the Briefs mobile
              // cards now treat body taps as expand-toggle).
              e.stopPropagation();
              handleCopy();
            }}
            data-testid="brief-message-viewer-copy"
            // Mobile: 28px-min control gives a tappable affordance instead
            // of the previous flush-text link. Desktop sizing is unchanged
            // through `text-xs` inheritance from the header container.
            className="inline-flex min-h-7 items-center rounded px-1 -mx-1 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {copied ? 'Copied' : 'Copy text'}
          </button>
          {traceUrl && (
            <a
              href={traceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-message-viewer-trace-link"
              className="ml-auto inline-flex min-h-7 items-center rounded px-1 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              onClick={(e) => e.stopPropagation()}
            >
              → open in Langfuse
            </a>
          )}
        </div>

        {/* Main message */}
        <section
          data-testid="brief-message-viewer-main"
          aria-label="Main brief message"
        >
          <MarkdownBody
            markdown={mainMd}
            testId="brief-message-viewer-main-body"
          />
        </section>

        {/* Thread replies */}
        {data.thread && data.thread.length > 0 && (
          <section
            data-testid="brief-message-viewer-thread"
            aria-label="Thread replies"
            className="space-y-3"
          >
            {data.thread.map((reply, idx) => {
              const replyMd = pickMarkdown(reply.blocks, reply.text);
              return (
                <div
                  key={idx}
                  data-testid="brief-message-viewer-reply"
                  data-reply-index={idx}
                  className="border-l-2 border-border pl-2 md:pl-3"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Reply {idx + 1}/{data.thread.length}
                  </div>
                  <MarkdownBody
                    markdown={replyMd}
                    testId={`brief-message-viewer-reply-body-${idx}`}
                  />
                </div>
              );
            })}
          </section>
        )}

        {/* Feedback prompt */}
        {feedbackHasContent && (
          <section
            data-testid="brief-message-viewer-feedback"
            aria-label="Feedback prompt"
            className="rounded border border-border bg-muted/40 p-3 space-y-2"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Feedback prompt
            </div>
            <MarkdownBody
              markdown={slackBlocksToMarkdown(
                // Action blocks become "*[buttons]*" inside the converter
                // — strip them before rendering markdown so we can show the
                // labels separately as inert chips below.
                (data.feedbackBlock ?? []).filter(
                  (b) => (b as { type?: string } | null)?.type !== 'actions',
                ),
              )}
              testId="brief-message-viewer-feedback-body"
            />
            {feedbackLabels.length > 0 && (
              <div
                data-testid="brief-message-viewer-feedback-actions"
                className="flex flex-wrap gap-1.5"
              >
                {feedbackLabels.map((label, idx) => (
                  <span
                    key={`${label}-${idx}`}
                    className="inline-flex items-center rounded border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
                    aria-disabled="true"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
          </section>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Briefs-tab viewer — keyed on (username, sent date). The canonical
 * "show me exactly what was sent" panel.
 */
export function BriefMessageViewer({
  userId,
  date,
  fallbackTraceId = null,
  enabled = true,
}: BriefMessageViewerProps): React.ReactElement {
  const { data, isLoading, error } = useBriefMessages(userId, date, enabled);
  return (
    <BriefMessageStateView
      data={data}
      isLoading={isLoading}
      error={error}
      fallbackTraceId={fallbackTraceId}
    />
  );
}

// ---------------------------------------------------------------------------
// By-trace viewer
// ---------------------------------------------------------------------------

export interface BriefMessageViewerByTraceProps {
  /** Langfuse trace id — `briefMessages.traceId`. */
  traceId: string;
  /**
   * Trace id to offer as a Langfuse fallback link on 404. Defaults to
   * `traceId` (the same trace we looked up) so a not-persisted brief still
   * links straight to its trace.
   */
  fallbackTraceId?: string | null;
  /** Gate the fetch — only when the host panel is expanded. Defaults true. */
  enabled?: boolean;
}

/**
 * Trace-surface viewer — keyed on a brief's trace id. Renders the SAME
 * canonical content as {@link BriefMessageViewer}; used by TraceDetailPanel so
 * the Feedback / Operations / any trace expand shows the full delivered brief
 * instead of the trace's bare status string ("sent N messages"). 404 ("not a
 * persisted brief") falls back to the Langfuse link.
 */
export function BriefMessageViewerByTrace({
  traceId,
  fallbackTraceId = null,
  enabled = true,
}: BriefMessageViewerByTraceProps): React.ReactElement {
  const { data, isLoading, error } = useBriefMessagesByTrace(traceId, enabled);
  return (
    <BriefMessageStateView
      data={data}
      isLoading={isLoading}
      error={error}
      fallbackTraceId={fallbackTraceId ?? traceId}
    />
  );
}
